import { useEffect } from 'react';
import { Tag } from 'antd';
import type { ColumnsType } from 'antd/lib/table';

import { useAppDispatch, useAppSelector } from '../../../../hooks';
import { TableService } from '../../../../service/table.service';
import type { Status } from '../../client-profile/client-tabs/graphs/graphSlice';
import { getPercentage } from '../program-dashboard.helper';
import type { TimeInStatus } from '../programDashboardSlice';
import { handleModalTableChange } from './modalDashboardSlice';

const getPercentageByStatus = (record: TimeInStatus, status: Status) => {
  const total = Object.values(record.cumulative_minutes_in_status).reduce((acc, v) => acc + v, 0);
  return getPercentage(record.cumulative_minutes_in_status[status], total);
};

export const GetTimeInRiskModalProps = () => {
  const dispatch = useAppDispatch();
  const { getColumnSearchProps, clearSearch } = TableService();
  const productFilter = useAppSelector((state) => state.tableSlice.productFilter);
  const sortedInfo = useAppSelector((state) => state.modalDashboardSlice.requestModalParam.sortedInfo);
  const filteredInfo = useAppSelector((state) => state.modalDashboardSlice.requestModalParam.filteredInfo);
  const { use_critical_risk, use_risk_nomenclature } = useAppSelector((state) => state.authSlice.authResult);

  useEffect(() => {
    dispatch(
      handleModalTableChange({
        sorter: {
          columnKey: 'full_name',
          field: 'full_name',
          order: 'ascend',
        },
      }),
    );
  }, [dispatch]);

  const baseColumns: ColumnsType<TimeInStatus> = [
    {
      title: 'Client name',
      dataIndex: 'full_name',
      key: 'full_name',
      sorter: (a, b) => a.full_name.localeCompare(b.full_name),
      onFilter: (value, record) => record.full_name.toLowerCase().includes((value as string).toLowerCase()),
      ...getColumnSearchProps('full_name', filteredInfo.full_name),
      sortOrder: sortedInfo.field === 'full_name' ? sortedInfo.order : null,
      filteredValue: filteredInfo.full_name || null,
      className: 'ph-no-capture',
    },
    {
      title: 'Client sites',
      dataIndex: 'sites',
      key: 'sites',
      width: 200,
      ...getColumnSearchProps('sites', filteredInfo.sites),
      onFilter: (value, record) => record.sites?.some((s) => s.toLowerCase().includes((value as string).toLowerCase())),
      render: (_text, record) =>
        record.sites?.map((site, index) => {
          return (
            <div key={index}>
              {site !== null ? (
                <Tag color="geekblue" style={{ border: 'none' }}>
                  {site}
                </Tag>
              ) : null}
            </div>
          );
        }),
      filteredValue: filteredInfo.sites || null,
    },
    {
      title: 'Products',
      dataIndex: 'products',
      key: 'products',
      filters: productFilter,
      onFilter: (value, record) => record.products?.includes(value as string),
      filteredValue: filteredInfo.products || null,
      width: 150,
      render: (_text, record) =>
        record.products?.map((product, index) => {
          return (
            <div key={index}>
              {product !== null ? (
                <Tag color="geekblue" style={{ border: 'none' }}>
                  {product}
                </Tag>
              ) : null}
            </div>
          );
        }),
    },
    {
      title: `Time spent in critical ${use_risk_nomenclature ? 'risk status' : 'status'}`,
      dataIndex: 'cumulative_minutes_in_status',
      key: 'critical_risk_time',
      align: 'center' as const,
      render: (_text, record) => <p> {getPercentageByStatus(record, 'critical_risk')}%</p>,
      sorter: (a, b) => getPercentageByStatus(a, 'critical_risk') - getPercentageByStatus(b, 'critical_risk'),
      sortOrder: sortedInfo.columnKey === 'critical_risk' ? sortedInfo.order : null,
    },
    {
      title: `Time spent in high ${use_risk_nomenclature ? 'risk status' : 'status'}`,
      dataIndex: 'cumulative_minutes_in_status',
      key: 'high_risk_time',
      align: 'center' as const,
      render: (_text, record) => <p> {getPercentageByStatus(record, 'high_risk')}%</p>,
      sorter: (a, b) => getPercentageByStatus(a, 'high_risk') - getPercentageByStatus(b, 'high_risk'),
      sortOrder: sortedInfo.columnKey === 'high_risk' ? sortedInfo.order : null,
    },
    {
      title: `Time spent in medium ${use_risk_nomenclature ? 'risk status' : 'status'}`,
      dataIndex: 'cumulative_minutes_in_status',
      key: 'medium_risk_time',
      align: 'center' as const,
      render: (_text, record) => <p> {getPercentageByStatus(record, 'medium_risk')}%</p>,
      sorter: (a, b) => getPercentageByStatus(a, 'medium_risk') - getPercentageByStatus(b, 'medium_risk'),
      sortOrder: sortedInfo.columnKey === 'medium_risk' ? sortedInfo.order : null,
    },
    {
      title: `Time spent in low ${use_risk_nomenclature ? 'risk status' : 'status'}`,
      dataIndex: 'cumulative_minutes_in_status',
      key: 'low_risk_time',
      align: 'center' as const,
      render: (_text, record) => <p> {getPercentageByStatus(record, 'low_risk')}%</p>,
      sorter: (a, b) => getPercentageByStatus(a, 'low_risk') - getPercentageByStatus(b, 'low_risk'),
      sortOrder: sortedInfo.columnKey === 'low_risk' ? sortedInfo.order : null,
    },
    {
      title: 'Time spent in incomplete status',
      dataIndex: 'cumulative_minutes_in_status',
      key: 'incomplete_time',
      align: 'center' as const,
      render: (_text, record) => <p> {getPercentageByStatus(record, 'incomplete')}%</p>,
      sorter: (a, b) => getPercentageByStatus(a, 'incomplete') - getPercentageByStatus(b, 'incomplete'),
      sortOrder: sortedInfo.columnKey === 'incomplete' ? sortedInfo.order : null,
    },
    {
      title: 'Time spent as inactive',
      dataIndex: 'cumulative_minutes_in_status',
      key: 'inactive_time',
      align: 'center' as const,
      render: (_text, record) => <p> {getPercentageByStatus(record, 'inactive')}%</p>,
      sorter: (a, b) => getPercentageByStatus(a, 'inactive') - getPercentageByStatus(b, 'inactive'),
      sortOrder: sortedInfo.columnKey === 'inactive' ? sortedInfo.order : null,
    },
  ];

  const columns = baseColumns.filter(({ key }) => {
    switch (key) {
      case 'critical_risk_time':
        return use_critical_risk;
      default:
        return true;
    }
  });

  return { columns, clearSearch };
};
